import { useTheme } from "expo-router/react-navigation";
import { Trophy } from "lucide-react-native";
import { FC, useMemo } from "react";
import { View } from "react-native";

import {
  Heatmap,
  HorizontalBarChart,
  useCopyableChart,
} from "@/components/charts";
import { Box, Card, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import SampleEditorialSummary from "./_SampleEditorialSummary";

/* ── Types ─────────────────────────────────────────────── */

export interface ClaimTestReportData {
  claims: { id: string; name: string }[];
  metrics: { id: string; label: string }[];
  perCellSampleSize: number[];
  /** Distinct respondents across the section, deduped on `persona.id`.
      Used for "(N respondents)" chart-title chips. */
  respondentCount: number;
  topBoxCounts: number[][]; // [claimIdx][metricIdx]
}

interface ClaimTestReportProps {
  data: ClaimTestReportData;
  showSampleEditorialSummary?: boolean;
}

/* ── Component ──────────────────────────────────────────── */

const ClaimTestReport: FC<ClaimTestReportProps> = ({
  data,
  showSampleEditorialSummary = false,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const primary = theme.colors.primary;

  const { chartRef: rankingRef, copyButton: rankingCopyButton } =
    useCopyableChart();
  const { chartRef: heatmapRef, copyButton: heatmapCopyButton } =
    useCopyableChart({ raster: true });

  const fmtPct = (n: number) =>
    i18n.numberToPercentage(n, {
      precision: 1,
      stripInsignificantZeros: false,
    });
  const fmtPctRound = (n: number) =>
    i18n.numberToPercentage(n, { precision: 0 });

  const topBoxRate: number[][] = useMemo(
    () =>
      data.claims.map((_, ci) => {
        const n = data.perCellSampleSize[ci] ?? 0;
        return data.metrics.map(
          (_, mi) => (n > 0 ? (data.topBoxCounts[ci]?.[mi] ?? 0) / n : 0) * 100
        );
      }),
    [data]
  );

  const composite: number[] = useMemo(
    () =>
      topBoxRate.map((row) =>
        row.length === 0 ? 0 : row.reduce((s, r) => s + r, 0) / row.length
      ),
    [topBoxRate]
  );

  const winnerIdx = useMemo(() => {
    let best = 0;
    composite.forEach((v, i) => {
      if (v > composite[best]) best = i;
    });
    return best;
  }, [composite]);

  // Order stimuli (claims) by composite score, descending, so the heatmap
  // rows line up with the ranking chart (which sorts the same way).
  const rankedOrder = useMemo(
    () =>
      data.claims.map((_, i) => i).sort((a, b) => composite[b] - composite[a]),
    [data.claims, composite]
  );

  // Stimuli (claims) are the heatmap rows, metrics the columns.
  // `topBoxCounts` is indexed `[claimIdx][metricIdx]`; rows follow
  // `rankedOrder`, so the lookup is `topBoxCounts[rankedOrder[rowIdx]][colIdx]`.
  const heatmapCells = useMemo(
    () =>
      rankedOrder.flatMap((claimIdx, rowIdx) =>
        data.metrics.map((_, colIdx) => ({
          column: colIdx,
          count: data.topBoxCounts[claimIdx]?.[colIdx] ?? 0,
          row: rowIdx,
        }))
      ),
    [data, rankedOrder]
  );

  const heatmapDenominator = useMemo(
    () =>
      data.perCellSampleSize.length > 0
        ? Math.max(...data.perCellSampleSize)
        : 0,
    [data.perCellSampleSize]
  );

  /* Deduped respondent count — see `ClaimTestReportData.respondentCount`.
     Each persona can see multiple claims (`cells_per_respondent > 1`),
     so summing `perCellSampleSize` would double-count them. */
  const totalRespondents = data.respondentCount;

  /* No answers yet: render the shell, hand charts empty arrays (they show
     their own "No data available"), and show "—" instead of a fake winner —
     mirroring how `PricingResult` shows "—" rather than a fabricated price. */
  const hasData = totalRespondents > 0;

  const rankingPoints = useMemo(
    () =>
      data.claims.map((c, idx) => ({
        count: data.perCellSampleSize[idx] ?? 0,
        label: c.name,
        tooltipLine: `${fmtPct(composite[idx])} · n = ${
          data.perCellSampleSize[idx] ?? 0
        }`,
        value: composite[idx],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, composite, i18n]
  );

  if (data.claims.length === 0) {
    return null;
  }

  return (
    <VStack className="gap-3">
      {showSampleEditorialSummary && (
        <SampleEditorialSummary
          body={i18n.t("useCasePage.claimTest.sampleReport.summary.body")}
          headline={i18n.t(
            "useCasePage.claimTest.sampleReport.summary.headline"
          )}
        />
      )}

      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-3 lg:flex-row lg:items-center lg:gap-5">
          <VStack className="gap-2 lg:flex-1">
            <HStack className="items-center gap-4">
              <Box
                className="w-14 h-14 rounded-2xl items-center justify-center shrink-0"
                style={{ backgroundColor: primary }}
              >
                <Trophy color="white" size={26} strokeWidth={2.2} />
              </Box>
              <VStack className="flex-1 gap-1">
                <Text className="text-2xs font-semibold text-primary-600 uppercase tracking-wide">
                  {i18n.t("useCasePage.claimTest.sampleReport.winner.badge")}
                </Text>
                <Text className="text-xl font-bold text-typography-900 tracking-tight">
                  {hasData ? data.claims[winnerIdx].name : "—"}
                </Text>
              </VStack>
            </HStack>
            <Text className="text-xs text-typography-500 lg:pl-[72px]">
              {i18n.t("useCasePage.claimTest.sampleReport.winner.subtitle")}
            </Text>
          </VStack>
          <VStack className="items-center lg:items-end gap-0">
            <Text
              className="text-3xl font-bold tracking-tight"
              style={{ color: primary }}
            >
              {hasData ? fmtPctRound(composite[winnerIdx]) : "—"}
            </Text>
            <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide">
              {i18n.t(
                "useCasePage.claimTest.sampleReport.winner.compositeLabel"
              )}
            </Text>
          </VStack>
        </VStack>
      </Card>

      <Box className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        <Card className="group flex-1 min-w-0 p-6 rounded-2xl border border-outline-200">
          {rankingCopyButton}
          <VStack className="gap-5">
            <VStack className="gap-1 pb-2">
              <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
                {`${i18n.t("useCasePage.claimTest.sampleReport.ranking.title")} (${i18n.t("dashboardNavigator.respondents", { count: totalRespondents })})`}
              </Text>
              <Text className="text-xs text-typography-400">
                {i18n.t("useCasePage.claimTest.sampleReport.ranking.subtitle")}
              </Text>
            </VStack>
            <View ref={rankingRef} style={{ width: "100%" }}>
              <HorizontalBarChart points={hasData ? rankingPoints : []} />
            </View>
          </VStack>
        </Card>

        <Card className="group flex-1 min-w-0 p-6 rounded-2xl border border-outline-200">
          {heatmapCopyButton}
          <VStack className="gap-5 flex-1">
            <VStack className="gap-1 pb-2">
              <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
                {`${i18n.t("useCasePage.claimTest.sampleReport.metrics.title")} (${i18n.t("dashboardNavigator.respondents", { count: totalRespondents })})`}
              </Text>
              <Text className="text-xs text-typography-400">
                {i18n.t("useCasePage.claimTest.sampleReport.metrics.subtitle")}
              </Text>
            </VStack>
            <Box className="flex-1 justify-center">
              <View ref={heatmapRef} style={{ width: "100%" }}>
                <Heatmap
                  cells={hasData ? heatmapCells : []}
                  columnLabels={hasData ? data.metrics.map((m) => m.label) : []}
                  rowLabels={
                    hasData ? rankedOrder.map((i) => data.claims[i].name) : []
                  }
                  totalRespondents={heatmapDenominator}
                />
              </View>
            </Box>
          </VStack>
        </Card>
      </Box>
    </VStack>
  );
};

export default ClaimTestReport;
